import React, { Suspense, lazy, useCallback, useId, useMemo, useState } from 'react'
import { DocumentTextIcon, EyeSlashIcon } from '@heroicons/react/24/outline'
import { CZCIONKA_FORMULARZA } from '../../config/zgloszeniaOsd'
import { pobranyDokument } from '../../lib/aktywnosc'
import { opisOplacalnosci, opisRysunkowTeczki, podsumowanieProjektu, rzutZProjektu } from '../../lib/teczka/dlaWykonawcy'
import { doSprawdzenia, normalizujMiejsca, wierszeMiejsc } from '../../lib/teczka/miejsca'
import { plural } from '../kits/format'

const PodgladRysunku = lazy(() => import('../rysunek/PodgladRysunku'))

/**
 * Teczka instalacji przy zleceniu (plan „VoltValue dla domu”, P5, 2026-10-24), tylko do odczytu: rzut dachu, moduły
 * i moc, sprzęt, produkcja i zwrot, miejsca w domu z listą „do sprawdzenia na miejscu”.
 *
 * `teczka` to widok z backendu (`teczka/prywatnosc.widok_migawki` albo `GET /api/v1/teczka/podglad-wykonawcy`): przed
 * kontaktem bez adresu, mapy, nazwy teczki, PPE i ceny posiadanej oferty. Komponent niczego nie dopowiada, więc to,
 * czego nie ma w widoku, nie pojawi się na ekranie.
 *
 * `wariant`: `wykonawca` (zlecenie widziane przez wykonawcę) albo `klient` (podgląd „Co zobaczą wykonawcy”).
 * `poKontakcie`: wykonawca wybrany przez klienta dostaje pełną teczkę, więc znika nota o ukrytych danych.
 */

const Wiersz = ({ etykieta, children }) => (
  <div className="grid grid-cols-[8.5rem_minmax(0,1fr)] gap-x-3 border-t border-gray-100 py-2 first:border-t-0 dark:border-gray-800 sm:grid-cols-[11rem_minmax(0,1fr)]">
    <dt className="text-[13px] text-gray-500 dark:text-gray-400">{etykieta}</dt>
    <dd className="m-0 min-w-0 text-sm font-medium text-gray-900 [overflow-wrap:anywhere] dark:text-gray-100">{children}</dd>
  </div>
)

const Sekcja = ({ tytul, dzialanie, children }) => {
  const id = useId()
  return (
    <section aria-labelledby={id} className="py-5 first:pt-0">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 id={id} className="m-0 text-base font-bold text-gray-900 dark:text-gray-100">{tytul}</h3>
        {dzialanie}
      </div>
      {children}
    </section>
  )
}

const Brak = ({ children }) => <p className="m-0 text-sm text-gray-500 dark:text-gray-400">{children}</p>

const TeczkaWykonawcy = ({ teczka, wariant = 'wykonawca', poKontakcie = false }) => {
  const [rysunek, setRysunek] = useState(null)
  const projekt = teczka?.projekt || null
  const rzut = useMemo(() => rzutZProjektu(projekt), [projekt])
  const s = useMemo(() => podsumowanieProjektu(projekt, rzut), [projekt, rzut])
  const kontekst = useMemo(() => ({ zMagazynem: Boolean(s?.magazynKwh) }), [s])
  const miejsca = teczka?.miejsca ? normalizujMiejsca(teczka.miejsca) : null
  const wiersze = useMemo(() => (miejsca ? wierszeMiejsc(miejsca, kontekst) : []), [miejsca, kontekst])
  const lista = useMemo(() => (miejsca ? doSprawdzenia(miejsca, kontekst) : []), [miejsca, kontekst])
  const opl = useMemo(() => opisOplacalnosci(teczka?.pieniadze), [teczka])
  const odpowiedzi = wiersze.filter((w) => w.tekst || w.opis)

  // Jeden PDF z całą teczką: rzut dachu, arkusze połaci, schemat instalacji i rozmieszczenie urządzeń (ciężkie moduły
  // rysunku ładują się dopiero przy otwarciu podglądu).
  const zbuduj = useCallback(async (format) => {
    const { rysujArkusze } = await import('../../lib/rysunek/arkusze')
    return rysujArkusze(opisRysunkowTeczki(projekt, teczka?.miejsca, { format, nrTeczki: teczka?.id ? `T-${teczka.id}` : '' }),
      CZCIONKA_FORMULARZA)
  }, [projekt, teczka])
  // Pobranie pliku przez wykonawcę zostaje w pomiarze i w dzienniku jego firmy; własny podgląd klienta nie.
  const przedPobraniem = wariant === 'wykonawca' ? async () => { pobranyDokument('teczka_zlecenie'); return true } : undefined

  if (!teczka || !s) {
    return <Brak>Klient nie dołączył jeszcze projektu dachu do tego zlecenia.</Brak>
  }

  const okolica = [s.miejscowosc, s.wojewodztwo].filter(Boolean).join(', ')

  return (
    <div className="divide-y divide-gray-100 dark:divide-gray-800">
      {!poKontakcie && (
        <p className="mb-4 flex items-start gap-2 text-[13px] leading-5 text-gray-600 dark:text-gray-300">
          <EyeSlashIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" aria-hidden="true" />
          {wariant === 'klient'
            ? 'Tak zobaczą Twoją teczkę wykonawcy zaproszeni do zlecenia. Adres, mapa, numer PPE i cena oferty, którą masz, zostają u Ciebie, dopóki nie wybierzesz wykonawcy.'
            : 'Bez adresu, mapy i numeru PPE. Dokładne dane dostaje wykonawca, którego wybierze klient.'}
        </p>
      )}

      <Sekcja tytul="Instalacja z projektu"
              dzialanie={rzut && (
                <button type="button" onClick={() => setRysunek(true)}
                        className="inline-flex min-h-[40px] items-center gap-2 rounded-lg border border-gray-300 bg-white px-3.5 text-sm font-semibold text-gray-800 hover:border-blue-300 hover:text-blue-700 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100">
                  <DocumentTextIcon className="h-4 w-4" aria-hidden="true" /> Rysunki teczki
                </button>
              )}>
        <dl className="m-0">
          {okolica && <Wiersz etykieta="Okolica">{okolica}</Wiersz>}
          <Wiersz etykieta="Rodzaj">{s.rodzaj === 'grunt' ? 'Instalacja wolnostojąca' : 'Na dachu'}</Wiersz>
          <Wiersz etykieta="Moc i moduły">
            <span className="tabular-nums">{String(Math.round(s.mocKwp * 100) / 100).replace('.', ',')} kWp</span>,{' '}
            <span className="tabular-nums">{s.liczba}</span> {plural(s.liczba, 'moduł', 'moduły', 'modułów')}
            {s.modul && <span className="block text-[13px] font-normal text-gray-600 dark:text-gray-300">{s.modul}</span>}
          </Wiersz>
          {s.polacie.filter((p) => p.liczba > 0).length > 0 && (
            <Wiersz etykieta="Połacie">
              <ul className="m-0 list-none space-y-0.5 p-0">
                {s.polacie.filter((p) => p.liczba > 0).map((p) => (
                  <li key={p.litera} className="font-normal">
                    <b className="font-semibold">{p.litera}</b>: {p.plaska ? 'dach płaski' : `${p.kierunek}, ${p.nachylenie}°`}, <span className="tabular-nums">{p.liczba}</span> {plural(p.liczba, 'moduł', 'moduły', 'modułów')}
                  </li>
                ))}
              </ul>
            </Wiersz>
          )}
          <Wiersz etykieta="Falownik">{s.falownik || <span className="font-normal text-gray-500">do doboru</span>}</Wiersz>
          <Wiersz etykieta="Magazyn energii">{s.magazyn || <span className="font-normal text-gray-500">bez magazynu</span>}</Wiersz>
        </dl>
      </Sekcja>

      <Sekcja tytul="Produkcja i zwrot">
        {opl ? (
          <>
            <dl className="m-0">
              {opl.rok && <Wiersz etykieta="Oszczędność w roku">{opl.rok}</Wiersz>}
              {opl.lata25 && <Wiersz etykieta="W 25 lat">{opl.lata25}</Wiersz>}
              {opl.zwrot && <Wiersz etykieta="Zwrot bez dotacji">{opl.zwrot}</Wiersz>}
              {opl.zwrotZDotacja && <Wiersz etykieta="Zwrot z dotacją">{opl.zwrotZDotacja}</Wiersz>}
              {opl.taryfa && opl.taryfa !== 'nie_wiem' && <Wiersz etykieta="Taryfa klienta">{opl.taryfa}</Wiersz>}
            </dl>
            <p className="mt-2 text-xs leading-5 text-gray-500 dark:text-gray-400">
              Szacunek klienta z VoltValue, widełki z założeniami z dnia obliczenia{opl.data ? ` (${opl.data})` : ''}. Nie jest ofertą ani gwarancją uzysku.
            </p>
          </>
        ) : <Brak>Klient nie policzył jeszcze opłacalności.</Brak>}
      </Sekcja>

      <Sekcja tytul="Licznik i rozdzielnica">
        {odpowiedzi.length > 0 ? (
          <>
            <dl className="m-0">
              {odpowiedzi.map((w) => (
                <Wiersz key={w.klucz} etykieta={w.tytul}>
                  {w.tekst || <span className="font-normal text-gray-500">bez odpowiedzi</span>}
                  {w.opis && <span className="block text-[13px] font-normal text-gray-600 dark:text-gray-300">{w.opis}</span>}
                </Wiersz>
              ))}
            </dl>
            {lista.length > 0 && (
              <details className="mt-3 rounded-lg border border-gray-200 px-3 py-2 dark:border-gray-700">
                <summary className="cursor-pointer text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Do sprawdzenia na miejscu: <span className="tabular-nums">{lista.length}</span> {plural(lista.length, 'punkt', 'punkty', 'punktów')}
                </summary>
                <ul className="mt-2 list-disc space-y-1 pl-5 text-[13px] leading-5 text-gray-700 dark:text-gray-300">
                  {lista.map((p) => <li key={p.klucz}>{p.tekst}</li>)}
                </ul>
              </details>
            )}
          </>
        ) : <Brak>Klient nie uzupełnił jeszcze danych o liczniku i rozdzielnicy. Wykonawca sprawdzi je na miejscu.</Brak>}
      </Sekcja>

      {rysunek && (
        <Suspense fallback={null}>
          <PodgladRysunku zbuduj={zbuduj} tytul="Rysunki teczki" nazwaPliku="teczka-rysunki"
                          przedPobraniem={przedPobraniem} onZamknij={() => setRysunek(null)} />
        </Suspense>
      )}
    </div>
  )
}

export default TeczkaWykonawcy
